import {
  HttpContextToken,
  HttpErrorResponse,
  HttpHeaders,
  HttpRequest,
} from '@angular/common/http';

import { environment } from 'src/environments/environment';
import { OutboxMethod } from '../models/offline-http.model';

/**
 * Which requests the offline layer handles, and how.
 *
 * Kept as data and pure functions, apart from the interceptor, so the whole
 * policy reads in one place and can be tested without an HTTP stack.
 *
 *   cache-read    A GET. Answered by the API while it is reachable, with the
 *                 response kept locally; answered from that local copy when not.
 *   queue-write   A teacher's write. Sent while reachable; stored in the outbox
 *                 and replayed against the same endpoint when not.
 *   network-only  Left alone.
 */
export type OfflinePolicy = 'cache-read' | 'queue-write' | 'network-only';

/** Set on a request's HttpContext to keep the offline layer out of it. */
export const OFFLINE_BYPASS = new HttpContextToken<boolean>(() => false);

/** Response header on anything the network did not produce: `cache` or `queued`. */
export const OFFLINE_SOURCE_HEADER = 'x-sam-offline';

/**
 * Never cached and never queued, whatever the method.
 */
const NETWORK_ONLY: readonly RegExp[] = [
  // The sync engine reconciles offline work itself; a remembered answer would
  // hand it stale cursors.
  /\/api\/v1\/sync(?:[/?]|$)/,
  // Parent connect is deliberately online-only.
  /\/api\/v1\/parents(?:[/?]|$)/,
  /\/school-connect/,
  /\/api\/v1\/consent(?:[/?]|$)/,
  // A session must reflect the server's answer now, never a remembered one.
  /\/(?:login|logout|authenticate|refresh-token|otp)(?:[/?]|$)/i,
  // Streamed replies; replaying one would fabricate an answer.
  /\/api\/v1\/virtual-assistant(?:[/?]|$)/,
  // Uploads need the binary and a live presigned URL.
  /\/media\/(?:presigned|upload)/,
];

/**
 * The writes a teacher can make offline.
 *
 * An allow-list rather than "every POST": replaying a write later is only safe
 * when the server's answer will not have changed its meaning in between, and
 * that has to be decided per endpoint. Anything absent here fails offline
 * exactly as it did before, which is honest - it does not pretend to be saved.
 */
const QUEUEABLE_WRITES: ReadonlyArray<{ method: OutboxMethod; path: RegExp }> =
  [
    {
      method: 'POST',
      path: /^\/api\/v1\/programs\/assessments\/teacher-assessment\/(?:save-response|save-bulk-responses|save-observations-bulk|bulk\/save-by-item)$/,
    },
    {
      method: 'POST',
      path: /^\/api\/v1\/programs\/assessments\/teacher\/intervals\/(?:start|force-next|complete-student)$/,
    },
    {
      method: 'POST',
      path: /^\/api\/v1\/teachers\/multiple-assessments\/bmc-results\/\d+\/ai-analysis$/,
    },
    {
      method: 'DELETE',
      path: /^\/api\/v1\/teachers\/multiple-assessments\/media\/\d+$/,
    },
  ];

/** Query parameters that exist only to defeat caches. */
const VOLATILE_PARAMS = new Set(['t', '_', 'ts', 'timestamp']);

/** Headers a replay must not carry over from the original request. */
const NON_REPLAYABLE_HEADERS = new Set(['authorization', 'idempotency-key']);

export function apiBase(): string {
  return (environment.apiUrl ?? '').replace(/\/+$/, '');
}

export function offlinePolicyFor(
  req: HttpRequest<unknown>,
  base: string = apiBase()
): OfflinePolicy {
  if (!base || !req.url.startsWith(base)) return 'network-only';
  if (NETWORK_ONLY.some(pattern => pattern.test(req.url)))
    return 'network-only';

  if (req.method === 'GET') {
    // Blob downloads (reports, exports) are large and the store is
    // quota-bound, so only JSON is kept.
    return req.responseType === 'json' ? 'cache-read' : 'network-only';
  }

  // IndexedDB cannot hold a FormData or a stream, so such a write cannot be
  // stored for later.
  if (!isStorableBody(req.body)) return 'network-only';

  const path = pathOf(req.url, base);
  return QUEUEABLE_WRITES.some(
    w => w.method === req.method && w.path.test(path)
  )
    ? 'queue-write'
    : 'network-only';
}

/** The request path below the API base, without query string or fragment. */
export function pathOf(url: string, base: string = apiBase()): string {
  const below = url.startsWith(base) ? url.slice(base.length) : url;
  const path = below.split(/[?#]/)[0].replace(/\/{2,}/g, '/');
  return path.startsWith('/') ? path : `/${path}`;
}

/**
 * The URL as a stable cache key: parameters sorted, cache-busters dropped.
 *
 * Without sorting, the same screen asking with its parameters in a different
 * order would miss its own copy offline; without dropping `t=`, every request
 * would be a new key and the cache would only ever grow.
 */
export function normaliseUrl(urlWithParams: string): string {
  const [withoutHash] = urlWithParams.split('#');
  const queryAt = withoutHash.indexOf('?');
  if (queryAt === -1) return withoutHash;

  const base = withoutHash.slice(0, queryAt);
  const kept: Array<[string, string]> = [];
  new URLSearchParams(withoutHash.slice(queryAt + 1)).forEach((value, name) => {
    if (!VOLATILE_PARAMS.has(name)) kept.push([name, value]);
  });
  kept.sort(([a, av], [b, bv]) =>
    a === b ? av.localeCompare(bv) : a.localeCompare(b)
  );

  const query = new URLSearchParams(kept).toString();
  return query ? `${base}?${query}` : base;
}

/**
 * Owner first, so one teacher's entries can never answer another's request.
 * `school-id` is part of the key because the API varies its answer on it.
 */
export function cacheKeyFor(
  userKey: string,
  urlWithParams: string,
  schoolId: string | null = null
): string {
  const school = schoolId ? `school:${schoolId}|` : '';
  return `${userKey}|${school}${normaliseUrl(urlWithParams)}`;
}

/** No response at all - the connection, not the server, failed. */
export function isNetworkFailure(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 0;
}

export function replayableHeaders(
  headers: HttpHeaders
): Record<string, string> {
  const kept: Record<string, string> = {};
  for (const name of headers.keys()) {
    if (NON_REPLAYABLE_HEADERS.has(name.toLowerCase())) continue;
    const value = headers.get(name);
    if (value !== null) kept[name] = value;
  }
  return kept;
}

/**
 * The body a screen receives for a write stored offline.
 *
 * Shaped like the API's own success envelope so existing `res.data?.…` reads
 * keep working; `data.offline` is how a screen tells "saved on this device"
 * from "saved on the server".
 */
export function queuedWriteBody(
  idempotencyKey: string
): Record<string, unknown> {
  return {
    success: true,
    status: 202,
    message:
      'Saved on this device. It will be sent when the connection returns.',
    data: { offline: true, queued: true, idempotency_key: idempotencyKey },
  };
}

/** Whether a write's response means it was stored locally rather than sent. */
export function isQueuedOffline(response: unknown): boolean {
  return (
    (response as { data?: { offline?: unknown } } | null)?.data?.offline ===
    true
  );
}

// ── Local-first reads ──────────────────────────────────────────────────────

/**
 * How long a local-first response is served without asking the server.
 *
 * Past this it is still served straight away - a teacher never waits on the
 * network for a screen they already have - and refreshed in the background so
 * the next open is current.
 */
export const LOCAL_FIRST_MAX_AGE_MS = 15 * 60_000;

/**
 *   structure  what an assessment is: its sections, questions, options, config.
 *              Authored by admins; only the age limit refreshes it.
 *   work       what the teacher has done: answers, results, progress, student
 *              lists. Also refreshed as soon as a save touches it.
 */
export type LocalFirstClass = 'structure' | 'work';

/** The teacher-assessment screens, which read their data locally first. */
const LOCAL_FIRST_PREFIXES: readonly RegExp[] = [
  /^\/api\/v1\/teachers\/multiple-assessments\//,
  /^\/api\/v1\/programs\/assessments\/(?:teacher-assessment|teacher|students)\//,
  /^\/api\/v1\/programs\/portfolios\/(?:history|linear-history)\//,
];

/**
 * Read network-first even on those screens. Media lists change through
 * uploads, which go straight to storage and never pass through the offline
 * layer, so nothing would tell a kept copy that it is out of date.
 */
const LOCAL_FIRST_EXCLUDED: readonly RegExp[] = [/\/media\//];

const STRUCTURE_PATHS: readonly RegExp[] = [
  /^\/api\/v1\/teachers\/multiple-assessments\/\d+\/sections$/,
  /^\/api\/v1\/teachers\/multiple-assessments\/section\/\d+\/items$/,
  /^\/api\/v1\/teachers\/multiple-assessments\/section-item\/\d+\/options$/,
  /^\/api\/v1\/teachers\/multiple-assessments\/school\/\d+$/,
  /^\/api\/v1\/programs\/assessments\/teacher\/techniques\//,
  /^\/api\/v1\/programs\/assessments\/teacher\/assessments\/\d+$/,
  /^\/api\/v1\/programs\/assessments\/teacher-assessment\/school-config(?:\/|$)/,
];

const ATTEMPT_PARAMS = ['attempt_id', 'attemptId'];

/** Whether, and how, a GET is served locally first. Null means network-first. */
export function localFirstClass(
  urlWithParams: string,
  base: string = apiBase()
): LocalFirstClass | null {
  if (!base || !urlWithParams.startsWith(base)) return null;
  const path = pathOf(urlWithParams, base);
  if (!LOCAL_FIRST_PREFIXES.some(pattern => pattern.test(path))) return null;
  if (LOCAL_FIRST_EXCLUDED.some(pattern => pattern.test(path))) return null;

  // The same sections/items/options URL carries a student's answers once it
  // names an attempt, and then it is work, not structure.
  const params = queryParams(urlWithParams);
  const namesAttempt = ATTEMPT_PARAMS.some(name => params.has(name));
  return STRUCTURE_PATHS.some(pattern => pattern.test(path)) && !namesAttempt
    ? 'structure'
    : 'work';
}

export function queryParams(urlWithParams: string): URLSearchParams {
  const [withoutHash] = urlWithParams.split('#');
  const queryAt = withoutHash.indexOf('?');
  return new URLSearchParams(
    queryAt === -1 ? '' : withoutHash.slice(queryAt + 1)
  );
}

function isStorableBody(body: unknown): boolean {
  if (body === null || body === undefined) return true;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return false;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return false;
  if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) return false;
  return ['object', 'string', 'number', 'boolean'].includes(typeof body);
}
